// backend/src/frontend/src/components/AgentPanel.tsx
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Box, Typography, IconButton, InputBase, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { tokens, monoStack } from '../theme';

// Agent id of the "Debugging Tool Agent" in slace_v2.agent_configs.
// The binary(10) id is an ASCII mnemonic (UNHEX('44656254416754723031') =
// "DebTAgTr01") and the core API addresses agents by that ASCII string.
export const DEBUG_AGENT_ID = 'DebTAgTr01';

const CORE_API = 'https://api.slace.com/core';
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 60;

interface ToolExecution {
  tool_name?: string;
  name?: string;
  input?: any;
  output?: any;
}

interface ChatRow {
  kind: 'user' | 'assistant' | 'tool' | 'error' | 'info';
  text: string;
  detail?: string;
}

interface AgentPanelProps {
  txId: string;
  open: boolean;
  onClose: () => void;
}

const AgentPanel: React.FC<AgentPanelProps> = ({ txId, open, onClose }) => {
  const [rows, setRows] = useState<ChatRow[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [expandedTools, setExpandedTools] = useState<Set<number>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const genRef = useRef(0);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [rows]);

  useEffect(() => {
    genRef.current += 1;
    const myGen = genRef.current;
    setRows([]);
    setSessionId(null);
    setExpandedTools(new Set());
    if (open && txId) {
      startSession(myGen);
    }
    return () => {
      genRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, txId]);

  const pushRow = (gen: number, row: ChatRow) => {
    if (genRef.current !== gen) return;
    setRows((prev) => [...prev, row]);
  };

  const startSession = async (gen: number) => {
    if (!DEBUG_AGENT_ID) {
      pushRow(gen, { kind: 'error', text: 'agent not configured: set DEBUG_AGENT_ID after applying the SQL' });
      return;
    }
    if (genRef.current !== gen) return;
    setBusy(true);
    try {
      const res = await axios.post(`${CORE_API}/agents/${DEBUG_AGENT_ID}/sessions`, {});
      if (genRef.current !== gen) return;
      const sid = res.data.id;
      setSessionId(sid);
      pushRow(gen, { kind: 'info', text: `session ${String(sid).slice(0, 8)} started` });
      await runMessage(gen, sid, `Analyze transaction ${txId} for errors and anomalies.`);
    } catch (e: any) {
      if (genRef.current !== gen) return;
      pushRow(gen, { kind: 'error', text: `session failed: ${e.response?.data?.message ?? e.message}` });
      setBusy(false);
    }
  };

  const runMessage = async (gen: number, sid: string, message: string) => {
    if (genRef.current !== gen) return;
    setBusy(true);
    pushRow(gen, { kind: 'user', text: message });
    try {
      const runRes = await axios.post(`${CORE_API}/agents/sessions/${sid}/run`, {
        input: message,
        execution: { mode: 'async' },
      });
      if (genRef.current !== gen) return;
      const runId = runRes.data.run_id;
      if (!runId) {
        renderRunResult(gen, runRes.data);
        setBusy(false);
        return;
      }
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (genRef.current !== gen) return;
        const jobRes = await axios.get(`${CORE_API}/agents/runs/${runId}`);
        if (genRef.current !== gen) return;
        const status = jobRes.data.status;
        if (status === 'completed') {
          renderRunResult(gen, jobRes.data.result ?? jobRes.data);
          setBusy(false);
          return;
        }
        if (status === 'failed') {
          pushRow(gen, { kind: 'error', text: `run failed: ${jobRes.data.error ?? 'unknown error'}` });
          setBusy(false);
          return;
        }
      }
      pushRow(gen, { kind: 'error', text: 'run timed out after 2 minutes' });
    } catch (e: any) {
      if (genRef.current !== gen) return;
      pushRow(gen, { kind: 'error', text: `run failed: ${e.response?.data?.message ?? e.message}` });
    }
    if (genRef.current !== gen) return;
    setBusy(false);
  };

  const renderRunResult = (gen: number, result: any) => {
    if (genRef.current !== gen) return;
    const tools: ToolExecution[] = result?.tool_executions ?? [];
    tools.forEach((t) => {
      const name = t.tool_name ?? t.name ?? 'tool';
      pushRow(gen, {
        kind: 'tool',
        text: `[tool] ${name}(${t.input ? JSON.stringify(t.input) : ''})`,
        detail: typeof t.output === 'string' ? t.output : JSON.stringify(t.output, null, 2),
      });
    });
    const output = result?.output;
    pushRow(gen, { kind: 'assistant', text: typeof output === 'string' ? output : JSON.stringify(output, null, 2) });
  };

  const handleSend = () => {
    const message = input.trim();
    if (!message || busy || !sessionId) return;
    setInput('');
    const myGen = genRef.current;
    runMessage(myGen, sessionId, message);
  };

  const rowColor = (kind: ChatRow['kind']) =>
    kind === 'user'
      ? tokens.onDark
      : kind === 'error'
      ? tokens.danger
      : kind === 'tool'
      ? tokens.onDarkMute
      : kind === 'info'
      ? tokens.onDarkMute
      : tokens.onDark;

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 56,
        right: 0,
        bottom: 0,
        width: 420,
        backgroundColor: tokens.surfaceDark,
        color: tokens.onDark,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: monoStack,
        zIndex: 1200,
        borderLeft: `1px solid ${tokens.hairlineStrong}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderBottom: `1px solid ${tokens.charcoal}`,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: tokens.onDark }}>[ AI DEBUGGER ]</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: tokens.onDarkMute }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
        {rows.map((row, i) => (
          <Box key={i} sx={{ mb: 1.5 }}>
            <Typography
              component="pre"
              onClick={() => {
                if (row.kind !== 'tool' || !row.detail) return;
                setExpandedTools((prev) => {
                  const next = new Set(prev);
                  if (next.has(i)) {
                    next.delete(i);
                  } else {
                    next.add(i);
                  }
                  return next;
                });
              }}
              sx={{
                color: rowColor(row.kind),
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
                fontSize: 13,
                fontFamily: monoStack,
                cursor: row.kind === 'tool' && row.detail ? 'pointer' : 'default',
              }}
            >
              {row.kind === 'user' ? `> ${row.text}` : row.text}
            </Typography>
            {row.kind === 'tool' && row.detail && expandedTools.has(i) && (
              <Typography
                component="pre"
                sx={{
                  color: tokens.ash,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  backgroundColor: tokens.surfaceDarkElevated,
                  borderRadius: '4px',
                  padding: '8px 12px',
                  margin: '4px 0 0 0',
                  fontSize: 12,
                  fontFamily: monoStack,
                  maxHeight: 240,
                  overflowY: 'auto',
                }}
              >
                {row.detail}
              </Typography>
            )}
          </Box>
        ))}
        {busy && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={12} sx={{ color: tokens.ash }} />
            <Typography sx={{ color: tokens.ash, fontSize: 13 }}>thinking...</Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          margin: '12px',
          padding: '8px 12px',
          backgroundColor: tokens.surfaceDarkElevated,
          borderRadius: '4px',
        }}
      >
        <Typography sx={{ color: tokens.ash, mr: 1 }}>|</Typography>
        <InputBase
          fullWidth
          placeholder={busy ? 'waiting for agent...' : 'ask about this transaction'}
          value={input}
          disabled={busy || !sessionId}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSend();
          }}
          sx={{ color: tokens.onDark, fontFamily: monoStack, fontSize: 14 }}
        />
      </Box>
    </Box>
  );
};

export default AgentPanel;
